import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { API_BASE_URL } from "../config/apiBase";
import { getAuthUserHeader, isAdminUser } from "../utils/userRoles";

const API = API_BASE_URL;

function toInt(v, fallback) {
  const n = parseInt(String(v ?? ""), 10);
  return Number.isNaN(n) ? fallback : n;
}

export default function LessonsPane({ studentId, isOpen, onClose, onLessonNavigate, routeBase }) {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const selectedWeek = toInt(params.weekId, 1);
  const selectedLesson = toInt(params.lessonId, 1);

  const [dashboard, setDashboard] = useState(null);
  const [curriculum, setCurriculum] = useState(null);
  const [lessonState, setLessonState] = useState(null);
  const [config, setConfig] = useState(null);
  const [error, setError] = useState("");

  const lessonsBase = routeBase || `/student/dashboard/${studentId}/lessons`;

  useEffect(() => {
    if (!studentId || !isOpen) return;
    let cancelled = false;

    async function load() {
      try {
        setError("");

        const [dashRes, curRes] = await Promise.all([
          fetch(`${API}/api/dashboard?student_id=${encodeURIComponent(studentId)}`),
          curriculum ? Promise.resolve(null) : fetch(`${API}/curriculum`),
        ]);

        fetch(`${API}/api/config`, {
          cache: "no-store",
          headers: {
            "X-User": getAuthUserHeader(),
          },
        })
          .then((r) => r.json())
          .then((j) => !cancelled && setConfig(j || null))
          .catch(() => !cancelled && setConfig(null));

        fetch(`${API}/api/lesson-state/${encodeURIComponent(studentId)}`)
          .then((r) => r.json())
          .then((j) => !cancelled && setLessonState(j || null))
          .catch(() => !cancelled && setLessonState(null));

        const dashJson = await dashRes.json();
        if (!cancelled) setDashboard(dashJson || null);

        if (!curriculum && curRes) {
          const curJson = await curRes.json();
          if (!cancelled) setCurriculum(curJson || null);
        }
      } catch {
        if (!cancelled) setError("Failed to load lessons.");
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [studentId, isOpen, location.pathname, curriculum]);

  const isAdminLessonTestMode = useMemo(() => {
    const enableAdminLessonTestMode = config?.ENABLE_ADMIN_LESSON_TEST_MODE === true;
    return enableAdminLessonTestMode && isAdminUser({
      student_id: localStorage.getItem("student_id"),
      role: localStorage.getItem("role"),
      is_admin: localStorage.getItem("is_admin") === "1",
    });
  }, [config]);

  const progress = useMemo(
    () => ({ currentWeek: toInt(dashboard?.week, 1), currentLesson: toInt(dashboard?.lesson, 1) }),
    [dashboard]
  );
  const nextLesson = useMemo(() => {
    if (!dashboard?.next_lesson || typeof dashboard.next_lesson !== "object") return null;
    const week = toInt(dashboard.next_lesson.week, null);
    const lesson = toInt(dashboard.next_lesson.lesson, null);
    return week && lesson ? { week, lesson } : null;
  }, [dashboard?.next_lesson]);

  const isWeekOverviewMode = useMemo(() => {
    const qs = new URLSearchParams(location.search || "");
    return qs.get("week_overview") === "1";
  }, [location.search]);

  const pendingReflectionWeek = useMemo(() => {
    const n = Number(dashboard?.pending_reflection_week);
    return Number.isNaN(n) ? null : n;
  }, [dashboard]);

  const needsWorkSet = useMemo(() => {
    const out = new Set();
    const lessons = Array.isArray(lessonState?.lessons) ? lessonState.lessons : [];
    for (const r of lessons) {
      if (!r?.needs_work) continue;
      const w = toInt(r.week, null);
      const l = toInt(r.lesson, null);
      if (w && l) out.add(`${w}:${l}`);
    }
    return out;
  }, [lessonState]);
  const nextLessonLocked =
    typeof dashboard?.next_lesson_locked === "boolean"
      ? dashboard.next_lesson_locked
      : Boolean(nextLesson);

  const goToLesson = useCallback(
    (week, lesson) => {
      const targetPath = `${lessonsBase}/week/${week}/lesson/${lesson}`;
      if (onLessonNavigate) {
        onLessonNavigate(targetPath);
        return;
      }
      navigate(targetPath);
    },
    [navigate, lessonsBase, onLessonNavigate]
  );

  const openWeekOverview = useCallback(
    (week, weekIntro) => {
      const weekNum = toInt(week, 1);
      navigate(`${lessonsBase}/week/${weekNum}/lesson/1?week_overview=1`, {
        state: {
          weekOverview: {
            week: weekNum,
            weekIntro: weekIntro || null,
          },
        },
      });
    },
    [navigate, lessonsBase]
  );

  return (
    <aside className="sl-list sp-lessons-pane" aria-label="All lessons">
      <div className="sl-list-header">
        <div className="sl-list-header-copy">
          <div className="sl-title">Lessons</div>
          <div className="sl-sub">Browse the full course and track completion.</div>
        </div>
        <button
          type="button"
          className="sl-close-btn"
          onClick={() => onClose?.()}
          aria-label="Close lessons panel"
          title="Close lessons panel"
        >
          ×
        </button>
      </div>

      <div className="sl-pane-body">
        {pendingReflectionWeek ? (
          <div className="lp-box lp-warn" style={{ margin: "10px 10px 8px" }}>
            Weekly Reflection for Week {pendingReflectionWeek} is required before Week{" "}
            {pendingReflectionWeek + 1} unlocks.
            <div style={{ marginTop: 8 }}>
              <button
                type="button"
                className="lp-btn lp-ghost"
                onClick={() => navigate(`${lessonsBase}/week-reflection/${pendingReflectionWeek}`)}
              >
                Open Weekly Reflection
              </button>
            </div>
          </div>
        ) : null}

        {error ? <div className="sl-error">{error}</div> : null}
        {!curriculum ? <div className="sl-loading">Loading lessons…</div> : null}

        {curriculum?.weeks?.map((w) => {
          const weekNum = toInt(w.week, 1);
          const isWeekLocked = weekNum > progress.currentWeek;
          return (
            <div key={w.week} className="sl-week">
              <div className="sl-week-heading">
                <div className="sl-week-title">Week {w.week}</div>
                {w.week_intro ? (
                  <button
                    type="button"
                    className={`sl-week-overview-btn ${
                      isWeekOverviewMode && selectedWeek === weekNum ? "is-active" : ""
                    }`.trim()}
                    onClick={() => !isWeekLocked && openWeekOverview(w.week, w.week_intro)}
                    aria-pressed={isWeekOverviewMode && selectedWeek === weekNum}
                    disabled={isWeekLocked}
                    title={isWeekLocked ? "Complete current week to unlock this overview." : ""}
                  >
                    View Week Overview
                  </button>
                ) : null}
              </div>

              <div className="sl-week-lessons">
                {(w.lessons || []).map((l) => {
                  const lessonNum = toInt(l.lesson, 1);
                  const currentLessonState = Array.isArray(lessonState?.lessons)
                    ? lessonState.lessons.find(
                        (r) =>
                          toInt(r?.week, null) === weekNum &&
                          toInt(r?.lesson, null) === lessonNum
                      )
                    : null;
                  const currentLessonClosed = Boolean(currentLessonState?.closed);
                  const isPast =
                    weekNum < progress.currentWeek ||
                    (weekNum === progress.currentWeek && lessonNum < progress.currentLesson) ||
                    (weekNum === progress.currentWeek &&
                      lessonNum === progress.currentLesson &&
                      currentLessonClosed);
                  const isCurrent =
                    weekNum === progress.currentWeek &&
                    lessonNum === progress.currentLesson &&
                    !currentLessonClosed;
                  const isBackendUnlockedNextLesson =
                    Boolean(
                      nextLesson &&
                        !nextLessonLocked &&
                        weekNum === nextLesson.week &&
                        lessonNum === nextLesson.lesson
                    );
                  const isLocked =
                    ((weekNum > progress.currentWeek) ||
                      (weekNum === progress.currentWeek && lessonNum > progress.currentLesson)) &&
                    !isBackendUnlockedNextLesson;
                  const canOpenLesson = isAdminLessonTestMode || !isLocked;
                  const isVisuallyLocked = !isAdminLessonTestMode && isLocked;
                  const isSelected = weekNum === selectedWeek && lessonNum === selectedLesson;
                  const isNeedsWork = needsWorkSet.has(`${weekNum}:${lessonNum}`);
                  const isReady = isBackendUnlockedNextLesson;

                  return (
                    <button
                      key={`${weekNum}-${lessonNum}`}
                      type="button"
                      className={`sl-lesson ${isSelected ? "is-selected" : ""}`.trim()}
                      disabled={isVisuallyLocked}
                      onClick={() => canOpenLesson && goToLesson(weekNum, lessonNum)}
                      title={
                        isVisuallyLocked
                          ? "Complete the current lesson to unlock this one."
                          : isPast
                          ? isNeedsWork
                            ? "Needs work — click to review"
                            : "Completed — click to review"
                          : isReady
                          ? "Ready to start"
                          : isCurrent
                          ? "Current lesson"
                          : ""
                      }
                      >
                        <span className={`sl-dot ${isPast ? "done" : isCurrent ? "now" : isReady ? "ready" : ""}`.trim()} aria-hidden />
                        <span className="sl-lesson-text">
                          <span className="sl-lesson-title">
                            Lesson {lessonNum}: {l.topic || l.title || ""}
                            {isAdminLessonTestMode ? (
                              <span
                                className="sl-admin-test-badge"
                                style={{
                                  marginLeft: 8,
                                  padding: "1px 6px",
                                  borderRadius: 999,
                                  fontSize: "0.7rem",
                                  lineHeight: 1.4,
                                  verticalAlign: "middle",
                                }}
                              >
                                Admin test
                              </span>
                            ) : null}
                          </span>
                          <span className="sl-lesson-status">
                            {isPast
                              ? isNeedsWork
                                ? "Needs work"
                              : "Completed"
                            : isCurrent
                            ? "Current"
                              : isReady
                              ? "Ready"
                              : isVisuallyLocked
                              ? "Locked"
                              : ""}
                          </span>
                        </span>
                      </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}
